import { IntlShape } from 'react-intl';
import * as yup from 'yup';
import {
  ExtensionEvent,
  ExtensionEventGroup,
  extensionFunctionType,
  extensionWebhookType,
  FunctionHook,
  FunctionRuntime,
  Hook,
  HookAction,
  HookType,
  WebhookHook,
} from '@rossum/api-client/hooks';

export type PublicExtensionFunction = FunctionHook & {
  config: {
    runtime: FunctionRuntime;
    code: string;
  };
};

const extesionIsOfType = (type: HookType, selectedExtension: Hook) =>
  selectedExtension.type === type;

const isWebhook = (selectedExtension: Hook): selectedExtension is WebhookHook =>
  extesionIsOfType(extensionWebhookType, selectedExtension);

// this guard is not accurate - it should guarantee that selectedExtension is PublicWebhookHook
// but we do not have this type at the moment
export const isPublicWebhook = (
  selectedExtension: Hook
): selectedExtension is WebhookHook =>
  isWebhook(selectedExtension) && !selectedExtension.config.private;

const isFunction = (
  selectedExtension: Hook
): selectedExtension is FunctionHook =>
  extesionIsOfType(extensionFunctionType, selectedExtension);

export const isPublicFunction = (
  selectedExtension: Hook
): selectedExtension is PublicExtensionFunction =>
  isFunction(selectedExtension) && !selectedExtension.config.private;

const urlStartsWithSchema = (value: string | undefined) =>
  value?.startsWith('http://') || value?.startsWith('https://') || false;

export const EXTENSION_DESCRIPTION_MAX_LENGTH = 2000;

export const webhookUrlValidation = (intl: IntlShape) =>
  yup
    .string()
    .required(
      intl.formatMessage({
        id: 'containers.settings.extensions.createExtension.url.required',
      })
    )
    .test(
      'missingSchema',
      intl.formatMessage({
        id: 'containers.settings.extensions.createExtension.url.missingSchema',
      }),
      urlStartsWithSchema
    );

export const webhookEvents = [
  {
    name: 'email',
    actions: ['received'],
  },
  {
    name: 'upload',
    actions: ['created'],
  },
  {
    name: 'annotation_status',
    actions: ['changed'],
  },
  {
    name: 'annotation_content',
    actions: [
      'initialize',
      'started',
      'updated',
      'confirm',
      'export',
      'user_update', // deprecated, only to be shown when the GET /hooks/{id} API response has it set in `hook.events` (set up before the change)
    ],
  },
  {
    name: 'invocation',
    actions: ['scheduled', 'manual', 'integration', 'interface'],
  },
] as const;

// Keep this so we can quickly hide events in the future?
export const visibleWebhookEvents = webhookEvents;

export const isExtensionEventGroup = (
  name: string
): name is ExtensionEventGroup => {
  return visibleWebhookEvents.some(event => event.name === name);
};

export const getDestructedEventAction = (eventAction: ExtensionEvent) => {
  const [name, action] = eventAction.split('.');

  return {
    name: name as ExtensionEventGroup,
    action: action as HookAction,
  };
};
